import { DestroyRef, inject, ResourceRef } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Reloads a resource on `environment.refreshIntervalMs` until its injection
 * context is destroyed. Ticks are skipped while the browser tab is hidden
 * unless `pollWhileHidden` is set. Becoming visible again reloads only if
 * the next interval is already due, so rapid visibility changes do not stack
 * extra fetches.
 *
 * Must be called from an injection context (field initializer or constructor).
 */
export function autoReloadResource(
  target: Pick<ResourceRef<unknown>, 'reload'>,
  intervalMs = environment.refreshIntervalMs,
  options?: { pollWhileHidden?: boolean },
): void {
  if (intervalMs <= 0) {
    return;
  }

  const destroyRef = inject(DestroyRef);
  const pollWhileHidden = options?.pollWhileHidden === true;
  /** Interval ticks skipped while hidden; visibility reload only if at least one was due. */
  let skippedTicks = 0;

  const onInterval = () => {
    if (!pollWhileHidden && document.hidden) {
      skippedTicks += 1;
      return;
    }
    skippedTicks = 0;
    target.reload();
  };

  const onVisibility = () => {
    if (pollWhileHidden || document.hidden || skippedTicks === 0) {
      return;
    }
    skippedTicks = 0;
    target.reload();
  };

  const timerId = window.setInterval(onInterval, intervalMs);
  document.addEventListener('visibilitychange', onVisibility);

  destroyRef.onDestroy(() => {
    window.clearInterval(timerId);
    document.removeEventListener('visibilitychange', onVisibility);
  });
}
